import { useCallback } from "react";
import {
  getConversationTags,
  assignTagsToConversation,
  unassignTagsFromConversation,
} from "@api/services/facebook";
import { assignAgentToConversation, unassignAgentFromConversation } from "@api/services/agents";
import { useAppDispatch } from "@stores/redux/store";
import { fetchAgentsByConversations, removeAgentFromConversation } from "@features/agents/agentsSlice";
import { useChatStore } from "@stores/chatStore";
import { useNotificationContext } from "@hooks/useNotificationContext";
import { PLATFORMS } from "@common";
import type { Agent } from "@interfaces/api.interface";

interface UseMenuHandlersParams {
  conversation: {
    lastMessage?: {
      conversationId?: string;
    };
  };
  tags: Array<{ description: string; id: number; tagName: string }>;
  agent?: Agent;
  selectedPageId: string | null;
  availableTags: Array<{ id: string; tagName: string; description: string }>;
  agents: Agent[];
  onTagsChange?: (
    tags: Array<{ description: string; id: number; tagName: string }>
  ) => void;
  onAgentChange?: (agent: Agent | null | undefined) => void;
}

export const useMenuHandlers = ({
  conversation,
  tags,
  agent,
  selectedPageId,
  availableTags,
  agents,
  onTagsChange,
  onAgentChange,
}: UseMenuHandlersParams) => {
  const dispatch = useAppDispatch();
  const { success, error: showError } = useNotificationContext();

  const handleTagToggle = useCallback(
    async (tagId: string) => {
      if (!selectedPageId || !conversation.lastMessage?.conversationId) return;

      const currentTagIds = tags.map((tag) => String(tag.id));
      const isSelected = currentTagIds.includes(tagId);
      const tagToToggle = availableTags.find((tag) => String(tag.id) === tagId);
      if (!tagToToggle) return;

      let newTags: Array<{
        description: string;
        id: number;
        tagName: string;
      }>;
      const tagIdNum = parseInt(tagId, 10);

      if (isSelected) {
        newTags = tags.filter((tag) => tag.id !== tagIdNum);
      } else {
        newTags = [
          ...tags,
          {
            id: parseInt(tagToToggle.id, 10),
            tagName: tagToToggle.tagName,
            description: tagToToggle.description,
          },
        ];
      }
      onTagsChange?.(newTags);
      let newTagIds: string[];
      if (isSelected) {
        newTagIds = currentTagIds.filter((id) => id !== tagId);
      } else {
        newTagIds = [...currentTagIds, tagId];
      }

      try {
        const externalId = conversation.lastMessage.conversationId;
        
        if (isSelected && tags.length === 1) {
          await unassignTagsFromConversation({
            externalId: externalId,
            platform: PLATFORMS.FACEBOOK,
            tagIds: [tagId],
            pageId: selectedPageId,
          });
        } else {
          await assignTagsToConversation({
            externalId: externalId,
            platform: PLATFORMS.FACEBOOK,
            tagIds: newTagIds,
            pageId: selectedPageId,
          });
        }
        
        const updatedTags = await getConversationTags({
          externalIds: externalId,
          platform: PLATFORMS.FACEBOOK,
          pageId: selectedPageId,
        });

        if (updatedTags) {
          onTagsChange?.(updatedTags);
        }
      } catch (error) {
        console.error("Error toggling tag:", error);
        onTagsChange?.(tags);
      }
    },
    [
      tags,
      selectedPageId,
      conversation.lastMessage,
      availableTags,
      onTagsChange,
    ]
  );

  const handleAgentSelect = useCallback(
    async (agentId: number) => {
      if (!selectedPageId || !conversation.lastMessage?.conversationId) return;

      const selectedAgent = agents.find((a) => a.id === agentId);
      if (!selectedAgent) return;
      
      const isRemoving = agent?.id === agentId;
      if (isRemoving) {
        return;
      }

      onAgentChange?.(selectedAgent || null);

      try {
        if (isRemoving) {
          return;
        }

        await assignAgentToConversation({
          agentId: agentId,
          externalId: conversation.lastMessage.conversationId,
          platform: PLATFORMS.FACEBOOK,
          pageId: selectedPageId,
        });

        if (selectedPageId && conversation.lastMessage.conversationId) {
          await dispatch(
            fetchAgentsByConversations({
              externalIds: [conversation.lastMessage.conversationId],
              platform: PLATFORMS.FACEBOOK,
              pageId: selectedPageId,
            })
          ).unwrap();
        }

        const triggerRefresh = useChatStore.getState().triggerRefreshConversationHistory;
        triggerRefresh();
        
        success("Gán agent thành công");
      } catch (error) {
        console.error("Error assigning agent:", error);
        onAgentChange?.(agent || null);
        showError(
          error instanceof Error
            ? error.message
            : "Không thể gán agent. Vui lòng thử lại."
        );
      }
    },
    [
      selectedPageId,
      conversation.lastMessage,
      agents,
      agent,
      onAgentChange,
      dispatch,
      success,
      showError,
    ]
  );

  const handleAgentRemove = useCallback(
    async () => {
      if (!selectedPageId || !conversation.lastMessage?.conversationId || !agent) return;

      onAgentChange?.(null);

      try {
        const externalId = conversation.lastMessage.conversationId;
        await unassignAgentFromConversation({
          externalId: externalId,
          platform: PLATFORMS.FACEBOOK,
          pageId: selectedPageId,
        });

        dispatch(removeAgentFromConversation(externalId));

        if (selectedPageId && externalId) {
          await dispatch(
            fetchAgentsByConversations({
              externalIds: [externalId],
              platform: PLATFORMS.FACEBOOK,
              pageId: selectedPageId,
            })
          ).unwrap();
        }

        const triggerRefresh = useChatStore.getState().triggerRefreshConversationHistory;
        triggerRefresh();
        
        success("Gỡ agent thành công");
      } catch (error) {
        console.error("Error removing agent:", error);
        onAgentChange?.(agent);
        showError(
          error instanceof Error
            ? error.message
            : "Không thể gỡ agent. Vui lòng thử lại."
        );
      }
    },
    [
      selectedPageId,
      conversation.lastMessage,
      agent,
      onAgentChange,
      dispatch,
      success,
      showError,
    ]
  );

  return {
    handleTagToggle,
    handleAgentSelect,
    handleAgentRemove,
  };
};
